import React, { Component } from "react";

const TEXT = "#2c3e50";
const TEXT_MUTED = "#8a9aac";
const BORDER = "#c5ced8";
const SELECTED_BG = "#4A86C1";
const MINUTE_STEP = 5;
const DURATION_HOURS = Array.from({ length: 24 }, (_, i) => i);
const TIME_HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * MINUTE_STEP);

function snapMinutes(mins) {
  const n = Math.max(0, Number(mins) || 0);
  return Math.round(n / MINUTE_STEP) * MINUTE_STEP;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function formatDuration(totalMins) {
  const n = Math.max(0, Number(totalMins) || 0);
  const h = Math.floor(n / 60);
  const m = n % 60;
  return `${pad2(h)}:${pad2(m)}`;
}

function formatTime(totalMins) {
  const n = ((Number(totalMins) % 1440) + 1440) % 1440;
  const h24 = Math.floor(n / 60);
  const m = n % 60;
  const ampm = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 || 12;
  return `${pad2(h12)}:${pad2(m)} ${ampm}`;
}

function splitDuration(totalMins) {
  const n = snapMinutes(Math.max(0, Number(totalMins) || 0));
  return {
    hours: Math.min(23, Math.floor(n / 60)),
    minutes: n % 60,
  };
}

function splitTime(totalMins) {
  const n = snapMinutes(((Number(totalMins) % 1440) + 1440) % 1440);
  const h24 = Math.floor(n / 60);
  const minutes = n % 60;
  const ampm = h24 >= 12 ? "PM" : "AM";
  const hours = h24 % 12 || 12;
  return { hours, minutes, ampm };
}

function joinDuration(hours, minutes) {
  return hours * 60 + minutes;
}

function joinTime(hours, minutes, ampm) {
  let h24 = hours % 12;
  if (ampm === "PM") h24 += 12;
  return h24 * 60 + minutes;
}

const fieldStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  height: 36,
  minWidth: 120,
  padding: "0 10px",
  borderRadius: 8,
  border: `1px solid ${BORDER}`,
  boxSizing: "border-box",
  background: "#fff",
  cursor: "pointer",
  fontFamily: "system-ui, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  fontSize: 14,
  color: TEXT,
  fontVariantNumeric: "tabular-nums",
  userSelect: "none",
};

const popupStyle = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: 0,
  zIndex: 30,
  display: "flex",
  gap: 8,
  padding: 10,
  background: "#fff",
  borderRadius: 10,
  border: `1px solid ${BORDER}`,
  boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
  fontFamily: "system-ui, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
};

const colStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  minWidth: 52,
};

const colLabelStyle = {
  fontSize: 11,
  letterSpacing: "0.06em",
  color: TEXT_MUTED,
  fontWeight: 600,
  marginBottom: 6,
  textAlign: "center",
};

const listWrapStyle = {
  display: "flex",
  alignItems: "stretch",
  gap: 4,
};

const listStyle = {
  height: 168,
  overflowY: "auto",
  overflowX: "hidden",
  scrollbarWidth: "thin",
  flex: 1,
};

const itemStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: 28,
  margin: "2px 0",
  borderRadius: 6,
  cursor: "pointer",
  fontSize: 14,
  color: TEXT,
  fontVariantNumeric: "tabular-nums",
  border: "none",
  background: "transparent",
  width: "100%",
  padding: 0,
  fontFamily: "inherit",
};

const itemSelectedStyle = {
  ...itemStyle,
  background: SELECTED_BG,
  color: "#fff",
  fontWeight: 700,
};

const scrollBtnColStyle = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  width: 14,
  flexShrink: 0,
};

const scrollBtnStyle = {
  border: "none",
  background: "transparent",
  color: TEXT_MUTED,
  cursor: "pointer",
  padding: 0,
  fontSize: 10,
  lineHeight: 1,
  height: 16,
};

function ClockIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" fill="none" stroke="#7a8794" strokeWidth="1.75" />
      <path d="M12 7v5l3.5 2" fill="none" stroke="#7a8794" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

class ScrollColumn extends Component {
  constructor(props) {
    super(props);
    this.listRef = React.createRef();
  }

  componentDidMount() {
    this.scrollSelectedIntoView(false);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value || prevProps.open !== this.props.open) {
      this.scrollSelectedIntoView(true);
    }
  }

  scrollSelectedIntoView(smooth) {
    const el = this.listRef.current;
    if (!el) return;
    const selected = el.querySelector("[data-selected='true']");
    if (selected && typeof selected.scrollIntoView === "function") {
      selected.scrollIntoView({
        block: "center",
        behavior: smooth ? "smooth" : "auto",
      });
    }
  }

  scrollBy(dir) {
    const el = this.listRef.current;
    if (!el) return;
    el.scrollBy({ top: dir * 84, behavior: "smooth" });
  }

  render() {
    const { label, options, value, formatOption, onSelect } = this.props;
    return (
      <div style={colStyle}>
        {label ? <div style={colLabelStyle}>{label}</div> : <div style={{ height: 17, marginBottom: 6 }} />}
        <div style={listWrapStyle}>
          <div ref={this.listRef} style={listStyle}>
            {options.map((opt) => {
              const selected = opt === value;
              return (
                <button
                  key={String(opt)}
                  type="button"
                  data-selected={selected ? "true" : "false"}
                  style={selected ? itemSelectedStyle : itemStyle}
                  onClick={() => onSelect(opt)}
                >
                  {formatOption ? formatOption(opt) : opt}
                </button>
              );
            })}
          </div>
          <div style={scrollBtnColStyle}>
            <button
              type="button"
              style={scrollBtnStyle}
              onClick={() => this.scrollBy(-1)}
              aria-label="Scroll up"
            >
              ▲
            </button>
            <button
              type="button"
              style={scrollBtnStyle}
              onClick={() => this.scrollBy(1)}
              aria-label="Scroll down"
            >
              ▼
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default class TimeDurSelect extends Component {
  constructor(props) {
    super(props);
    this.rootRef = React.createRef();
    this.state = { open: false };
  }

  componentDidMount() {
    document.addEventListener("mousedown", this.onDocDown);
    document.addEventListener("keydown", this.onDocKey);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.onDocDown);
    document.removeEventListener("keydown", this.onDocKey);
  }

  onDocDown = (e) => {
    if (!this.state.open) return;
    const root = this.rootRef.current;
    if (root && !root.contains(e.target)) {
      this.setState({ open: false });
    }
  };

  onDocKey = (e) => {
    if (e.key === "Escape" && this.state.open) {
      this.setState({ open: false });
    }
  };

  isDuration() {
    return this.props.mode === "duration";
  }

  getParts() {
    const value = this.props.value;
    if (this.isDuration()) return splitDuration(value);
    return splitTime(value);
  }

  commit(parts) {
    const { onChange } = this.props;
    if (typeof onChange !== "function") return;
    const next = this.isDuration()
      ? joinDuration(parts.hours, parts.minutes)
      : joinTime(parts.hours, parts.minutes, parts.ampm);
    onChange(next);
  }

  setHours = (hours) => {
    this.commit({ ...this.getParts(), hours });
  };

  setMinutes = (minutes) => {
    this.commit({ ...this.getParts(), minutes });
  };

  setAmpm = (ampm) => {
    this.commit({ ...this.getParts(), ampm });
  };

  toggleOpen = () => {
    this.setState((prev) => ({ open: !prev.open }));
  };

  renderPopup() {
    const parts = this.getParts();
    const duration = this.isDuration();

    return (
      <div style={popupStyle} role="dialog" aria-label={duration ? "Duration picker" : "Time picker"}>
        <ScrollColumn
          open={this.state.open}
          label={duration ? "HRS" : "HR"}
          options={duration ? DURATION_HOURS : TIME_HOURS}
          value={parts.hours}
          onSelect={this.setHours}
        />
        <ScrollColumn
          open={this.state.open}
          label="MIN"
          options={MINUTES}
          value={parts.minutes}
          formatOption={pad2}
          onSelect={this.setMinutes}
        />
        {!duration && (
          <div style={colStyle}>
            <div style={{ height: 17, marginBottom: 6 }} />
            <div style={{ ...listStyle, overflow: "visible", height: "auto" }}>
              {["AM", "PM"].map((p) => {
                const selected = parts.ampm === p;
                return (
                  <button
                    key={p}
                    type="button"
                    style={selected ? itemSelectedStyle : itemStyle}
                    onClick={() => this.setAmpm(p)}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  render() {
    const { value, style, className, disabled } = this.props;
    const duration = this.isDuration();
    const display = duration ? formatDuration(value) : formatTime(value);
    const { open } = this.state;

    return (
      <div
        ref={this.rootRef}
        className={className}
        style={{ position: "relative", display: "inline-block", ...style }}
      >
        <button
          type="button"
          disabled={disabled}
          style={{
            ...fieldStyle,
            opacity: disabled ? 0.6 : 1,
            cursor: disabled ? "default" : "pointer",
            width: "100%",
          }}
          onClick={this.toggleOpen}
          aria-expanded={open}
          aria-haspopup="dialog"
        >
          <span>{display}</span>
          <ClockIcon />
        </button>
        {open && !disabled ? this.renderPopup() : null}
      </div>
    );
  }
}

export {
  formatDuration,
  formatTime,
  snapMinutes,
  MINUTE_STEP,
};
